import { CallbackDataParams } from 'echarts/types/dist/shared'
import type { XAXisOption, YAXisOption } from 'echarts/types/dist/shared'
import { ChartOptions } from '@/types/echart'
import { JSONPath } from 'jsonpath-plus'
import set from 'lodash/set'
import { dayjs } from '@/composables/useDayjs'
import Mustache from 'mustache'
import { getActiveLayoutColors } from '@/composables/globalData/useLayouts'
import { WidgetExtended } from '@/types/dashboard'
import { getWidgetDateRange } from '@/helpers/widgetUtils'
import env from '@/env'
import i18n from '@/i18n'

interface Match {
    parent?: Record<string, unknown>
    parentProperty?: string
    value?: unknown
}

type AxisOption = XAXisOption | XAXisOption[] | YAXisOption | YAXisOption[]

/**
 * Type guard to check if value is a valid axis option
 */
function isAxisOption (value: unknown): value is AxisOption {
    if (!value) return false

    const axisConfig = Array.isArray(value) ? value[0] : value

    return typeof axisConfig === 'object' && axisConfig !== null
}

/**
 * Extracts raw axis data from axis option
 * Handles both single axis and array of axes configurations
 */
function getRawAxisData (axis: unknown): unknown[] | undefined {
    if (!isAxisOption(axis)) return undefined

    const axisConfig = Array.isArray(axis) ? axis[0] : axis

    if (axisConfig && 'data' in axisConfig && Array.isArray(axisConfig.data)) {
        return axisConfig.data
    }

    return undefined
}

/**
 * Type guard for string array
 */
function isStringArray (value: unknown): value is string[] {
    return Array.isArray(value) && value.every(item => typeof item === 'string')
}

/**
 * Extracts string axis data (e.g., category labels)
 */
function getStringAxisData (axis: unknown): string[] | undefined {
    const data = getRawAxisData(axis)

    return isStringArray(data) ? data : undefined
}

/**
 * Context passed to formatter functions via `bind`
 * Contains both the widget data and chart options for access within formatter functions
 */
interface TooltipFormatterFunctionContext {
    data: WidgetExtended
    options: ChartOptions
}

/**
 * Because we are expecting the API return the whole chart options as a JSON, we cannot expect the functions which we need in some cases
 * For that we did implement the mechanism that the BE will specify for the options we need a special strings that we will look for
 * And replace with our front-end defined functions
 */

/**
 * Checks if a string is a valid phone number (digits only, optionally with leading +)
 */
function isValidPhoneNumber (value: string): boolean {
    return /^\+?\d+$/.test(value)
}

/**
 * Builds a CDR (Call Detail Record) link for a given phone number using widget date range
 * Uses Mustache template from VITE_APP_CDR_SEARCH_URL_TEMPLATE env variable
 * Available template variables: {{fromDate}}, {{toDate}}, {{phoneNumber}}
 */
function buildCdrLink (phoneNumber: string, widget: WidgetExtended): string | undefined {
    const template = env.VITE_APP_CDR_SEARCH_URL_TEMPLATE

    if (!template) {
        return undefined
    }

    const { start, end } = getWidgetDateRange(widget)
    const dateFormat = 'DD.MM.YYYY'

    return Mustache.render(template, {
        fromDate: dayjs(start).format(dateFormat),
        toDate: dayjs(end).format(dateFormat),
        phoneNumber
    })
}

/* Formatter Functions */
function formatHeatmapTooltip (this: TooltipFormatterFunctionContext, params: CallbackDataParams) {
    const dateInMilliseconds = +params.name.split(':')[0]

    const date = new Intl.DateTimeFormat([], {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).format(dateInMilliseconds)

    const value = params.value

    if (!value || !Array.isArray(value)) {
        return ''
    }

    const rowIndex = value[1]
    const cellValue = value[2]

    let tooltipContent = `<p><span>${date}</span><span style="font-weight: bold; margin-left: 1em">${cellValue}</span></p>`

    if (typeof rowIndex === 'number' && this.options && this.data) {
        const yAxisData = getStringAxisData(this.options.yAxis)
        const clientPhone = yAxisData?.[rowIndex]

        if (clientPhone) {
            const isPhoneNumber = isValidPhoneNumber(clientPhone)
            const cdrLink = isPhoneNumber ? buildCdrLink(clientPhone, this.data) : undefined

            if (cdrLink) {
                const viewDetailsText = i18n.global.t('general.viewDetails')
                const { primary } = getActiveLayoutColors()
                tooltipContent += `<p>${clientPhone} - <a href="${cdrLink}" target="_blank" style="color: ${primary}; text-decoration: underline;">${viewDetailsText}</a></p>`
            } else {
                tooltipContent += `<p>${clientPhone}</p>`
            }
        }
    }

    return tooltipContent
}

function formatHeatmapXAxisLabel (value: string) {
    const [ dateInMilliseconds, splitByDay ] = value.split(':')

    const hoursAndMinutes = new Intl.DateTimeFormat([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(+dateInMilliseconds)

    if (+splitByDay >= 0) {
        return new Intl.DateTimeFormat([], {
            month: '2-digit',
            day: '2-digit',
            hour12: false,
        }).format(+dateInMilliseconds)
    }

    return hoursAndMinutes
}

function xAxisLabelInterval (index: number, value: string) {
    const splitByDay = value.split(':')[1]

    if(+splitByDay >= 0) {
        return +splitByDay
    }

    return index % 5 === 0
}

function formatDataZoomLabel (value: string, valueStr: string) {
    return formatHeatmapXAxisLabel(valueStr)
}

function getTextColorFromLayout () {
    const { mainTextColor } = getActiveLayoutColors()

    return mainTextColor
}

function modifyXAxisData (value: unknown): unknown {
    if (!isStringArray(value)) {
        return value
    }

    let lastDay: string | null = null

    return value.map(item => {
        const [ dateInMilliseconds, splitByDay ] = item.split(':')

        if(+splitByDay < 0) return item

        const dayStr = Intl.DateTimeFormat([], {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour12: false,
        }).format(+dateInMilliseconds)

        const isNewDay = dayStr !== lastDay

        if(isNewDay) {
            lastDay = dayStr

            return `${dateInMilliseconds}:1`
        }

        return `${dateInMilliseconds}:0`
    })
}

interface BaseFunctionMapping {
    /**
     * Path to the option property to map the value if expectedValue eq to value by paths.
     * Supports any valid jsonpath-plus syntax
     */
    jsonPaths: string | string[]
    /**
     * The name of the property we expect to have in specified path to check if the fn should be run.
     * Can be empty which tells to run fn always no matter what value it has
     */
    expectedValue?: string

    /**
     * If the property by the path is not there - create the needed structure and set it.
     * Will not work with complex json path that uses wildcard syntax
     */
    shouldSetPropertyIfDoesNotExist?: boolean
}
/**
 * Interface for mapping functions that update values using a provided function
 * @interface UpdateValueFunctionMapping
 * @extends {BaseFunctionMapping}
 */
interface UpdateValueFunctionMapping extends BaseFunctionMapping {
    /**
     * Function to run on the properties value to modify it
     * @param {unknown} value - Current value of the property
     * @returns {unknown} The modified value
     * @memberof UpdateValueFunctionMapping
     */
    updateFn: (value: unknown) => unknown
}
/**
 * Interface for mapping functions that replace values with predefined ones
 * @interface ValueToSetFunctionMapping
 * @extends {BaseFunctionMapping}
 */
interface ValueToSetFunctionMapping extends BaseFunctionMapping {
    /**
     * The value with which to replace the original value
     * @type {unknown}
     * @memberof ValueToSetFunctionMapping
     */
    valueToSet: unknown
}
type FunctionMapping = UpdateValueFunctionMapping | ValueToSetFunctionMapping

const FUNCTIONS_MAP: FunctionMapping[] = [
    {
        jsonPaths: '$.xAxis.data',
        updateFn: modifyXAxisData,
    },
    {
        jsonPaths: '$.tooltip.formatter',
        expectedValue: 'formatHeatmapTooltip',
        valueToSet: formatHeatmapTooltip,
    },
    {
        jsonPaths: '$.xAxis.axisLabel.formatter',
        expectedValue: 'formatHeatmapXAxisData',
        valueToSet: formatHeatmapXAxisLabel,
    },
    {
        jsonPaths: [ '$.xAxis.axisLabel.interval', '$.xAxis.splitLine.interval' ],
        expectedValue: 'xAxisLabelInterval',
        valueToSet: xAxisLabelInterval,
    },
    {
        jsonPaths: [ '$.dataZoom[0].labelFormatter' ],
        valueToSet: formatDataZoomLabel,
    },
    {
        jsonPaths: [ '$.graphic[*].children[*].style.fill', '$.dataZoom[*].textStyle.color', '$.visualMap.textStyle.color' ],
        updateFn: getTextColorFromLayout,
    },
    {
        jsonPaths: [ '$.legend.textStyle.color', '$.textStyle.color' ],
        shouldSetPropertyIfDoesNotExist: true,
        updateFn: getTextColorFromLayout,
    },
]

export function formatEchartOptions (options: ChartOptions, data: WidgetExtended): ChartOptions {
    const newOptions: ChartOptions = { ...options }

    FUNCTIONS_MAP.forEach(mapping => {
        const paths = Array.isArray(mapping.jsonPaths) ? mapping.jsonPaths : [ mapping.jsonPaths ]

        paths.forEach(jsonPath => {
            const matches: Array<Match> = JSONPath({
                path: jsonPath,
                json: newOptions,
                resultType: 'all'
            })

            if (matches.length > 0) {
                matches.forEach(match => {
                    if (!match.parent || !match.parentProperty) {
                        return
                    }

                    if (!mapping.expectedValue || match.value === mapping.expectedValue) {
                        const formatterContext: TooltipFormatterFunctionContext = {
                            data,
                            options
                        }

                        match.parent[match.parentProperty] = 'updateFn' in mapping
                            ? mapping.updateFn(match.value)
                            : typeof mapping.valueToSet === 'function'
                                ? mapping.valueToSet.bind(formatterContext)
                                : mapping.valueToSet
                    }
                })
            } else if (mapping.shouldSetPropertyIfDoesNotExist) {
                // If no match is found and the path is simple (without wildcards),
                // remove the '$.' prefix and set the value using lodash's set function.
                if (!jsonPath.includes('*')) {
                    const pathWithoutDollar = jsonPath.replace(/^\$\./, '')
                    const valueToSet = 'updateFn' in mapping
                        ? mapping.updateFn(undefined)
                        : mapping.valueToSet

                    set(
                        newOptions,
                        pathWithoutDollar,
                        valueToSet
                    )
                }
            }
        })
    })

    return newOptions
}
